// Production · Tuile EN COURS — BLOC UNIFIÉ (contrat gate 7b) : client/fallback,
// heure, statut de délai, pizzas et modifications réunis. Nom des pizzas très
// lisible à distance conservé (Jost bold XXL) ; hauteur/vide non figés — densité
// arbitrée au gate sur captures. Badges modifs contrastés (parseNotes), note
// libre de SA pizza (NotePizza), ligne ☆ habitude (Cormorant, ≤ 60 c), badge ×N
// si train. Statique, zéro animation, jamais de téléphone (RGPD §8).
import { nomAffichage } from '@/lib/affichage-commande';
import { parseNotes } from '@/lib/notes-convention';
import { formatHeure, formatMod } from '@/lib/format';
import { NOTE_MAX_TUILE, NotePizza } from '@/screens/shared/NotePizza';
import { StatutDelai } from '@/screens/shared/StatutDelai';
import type { StatutDelai as StatutDelaiMoteur } from '@/lib/heure-capacite';
import type { TuileT2 } from '@/hooks/useT2File';

interface Props {
  tuile: TuileT2;
  nomParId: Map<number, string>;
  numeros: ReadonlyMap<number, number>;
  delai: StatutDelaiMoteur | undefined;
  /** « Tout est prêt » (7f) — porte sur TOUTE la commande de la tuile. */
  onToutPret: () => void;
  toutPretVerrouille: boolean;
  toutPretErreur: boolean;
}

export function TuileEnCours({
  tuile,
  nomParId,
  numeros,
  delai,
  onToutPret,
  toutPretVerrouille,
  toutPretErreur,
}: Props) {
  const { commande, lignes, habitude, trainN } = tuile;
  return (
    <div
      data-testid="tuile-en-cours"
      className="relative mx-4 flex flex-col gap-4 rounded-lg bg-card p-6"
    >
      {trainN > 1 && (
        <span
          aria-label={`Train de ${trainN} commandes`}
          className="absolute top-4 right-4 rounded-full bg-ardoise px-5 py-2 font-mono text-3xl text-creme"
        >
          ×{trainN}
        </span>
      )}

      {/* En-tête du bloc : client/fallback · heure · statut de délai. */}
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <span
          className={
            commande.client_nom_snapshot
              ? 'font-cormorant text-4xl italic'
              : 'font-jost text-3xl font-medium'
          }
        >
          {nomAffichage(commande, numeros)}
        </span>
        <span className="font-mono text-3xl">
          {formatHeure(commande.heure_retrait_proposee)}
        </span>
        <StatutDelai delai={delai} className="text-3xl" />
      </div>

      <div className="flex flex-col gap-4">
        {lignes.map((l) => {
          const parsed = parseNotes(l.notes_libres ?? '');
          return (
            <div key={l.id} className="flex flex-col gap-2">
              <span className="font-jost text-6xl font-bold">
                {l.qty}× {nomParId.get(l.pizzaId) ?? `Pizza #${l.pizzaId}`}
              </span>
              {parsed.mod.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {parsed.mod.map((m, i) => (
                    <span
                      key={`${m}-${i}`}
                      className="rounded-md bg-rose px-4 py-1 font-jost text-3xl font-bold text-ardoise"
                    >
                      {formatMod(m)}
                    </span>
                  ))}
                </div>
              )}
              <NotePizza
                texte={parsed.texte}
                max={NOTE_MAX_TUILE}
                className="text-3xl"
              />
            </div>
          );
        })}
      </div>

      {habitude && (
        <p className="font-cormorant text-3xl italic text-ardoise">
          ☆ {habitude.slice(0, 60)}
        </p>
      )}

      {/* « Tout est prêt » (7f) — TOUTE la commande de la tuile (le nom est
          déjà dans l'en-tête, non répété). La tuile n'affiche jamais une
          commande prete/terminale (useT2File) — le bouton n'y apparaît donc
          jamais pour ces statuts. Bouton MAISON, zéro animation. */}
      <button
        type="button"
        data-testid="tout-est-pret"
        disabled={toutPretVerrouille}
        onClick={onToutPret}
        className="min-h-[96px] w-full rounded-lg bg-ardoise font-jost text-2xl text-creme"
      >
        Tout est prêt
      </button>
      {toutPretErreur && (
        <p
          data-testid="erreur-tout-pret"
          className="font-jost text-lg font-bold text-destructive"
        >
          Échec de la préparation — réessayer
        </p>
      )}
    </div>
  );
}
